import { QueryClient } from '@tanstack/vue-query'
import type { AxiosResponse } from 'axios'
import { type Ref } from 'vue'
import { ResponseOptions, useResponseHandler } from './response-handler'

/**
 * Global query client for the entire application
 * Configured with error handling and standard parameters
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error) => {
        // Don't retry on authentication errors
        if (error && typeof error === 'object' && 'code' in error && error.code === 2000) {
          return false
        }
        // Retry up to 2 times for other errors
        return failureCount < 2
      },
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
      throwOnError: false
    },
    mutations: {
      retry: false, // Don't retry mutations by default
      throwOnError: false
    }
  }
})

/**
 * Convenient function for invalidating multiple queries
 */
export const invalidateQueries = (queryKeys: string[]) => {
  queryKeys.forEach((key) => {
    queryClient.invalidateQueries({ queryKey: [key] })
  })
}

/**
 * Extract data with error tracking
 *
 * Two usage patterns:
 * 1. extractData(response) - basic extraction, no tracking
 * 2. extractData(response, component, action) - with tracking
 */
export function extractData<T>(
  response: AxiosResponse<{ code: number; data: T; message: string }>,
  component?: string,
  action?: string
): T {
  const { handleSuccess } = useResponseHandler()

  if (component && action) {
    return handleSuccess(response, ResponseOptions.withTracking(component, action))
  }

  return handleSuccess(response)
}

/**
 * Extract data with success toast
 */
export function extractDataWithToast<T>(
  response: AxiosResponse<{ code: number; data: T; message: string }>,
  successMessage?: string
): T {
  const { handleSuccess } = useResponseHandler()
  return handleSuccess(response, {
    showSuccessToast: true,
    successMessage
  })
}

/**
 * Registers listener for updating ref when data changes in queryClient
 */
export function syncQueryData<T>(
  queryKey: unknown[],
  targetRef: Ref<T | undefined>,
  defaultValue?: T
) {
  const queryData = queryClient.getQueryData(queryKey) as T | undefined

  if (queryData !== undefined) {
    targetRef.value = queryData
  } else if (defaultValue !== undefined) {
    targetRef.value = defaultValue
  }
}
